import { useId, useRef, useState, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { WHATIF_GLOSSARY } from '@/lib/whatifGlossary';
import { Info } from 'lucide-react';

/**
 * InfoTip — visible hover tooltip with full viewport clamping + text wrap.
 *
 * Behaviour (matches the HoverBar pattern used across the stock modal):
 *   - Desktop:  show on mouseenter / focus; hide on leave / blur
 *   - Mobile:   tap to open (stopPropagation); auto-close after 2.5s
 *
 * Positioning:
 *   1. Compute effective bubble width =  min(maxWidth, viewportW - 16)
 *      so even a 320px-wide iPhone SE can never make the bubble wider
 *      than the screen minus 8px gutters on each side.
 *   2. Desired center = trigger's horizontal midpoint.
 *   3. Clamp `left` to [8, viewportW - w - 8] so the bubble can NEVER
 *      overflow the viewport left/right edges.
 *   4. Flip above when there isn't vertical room below.
 *
 * Content:
 *   - `word-break: break-word` + `overflow-wrap: anywhere` so even long
 *     CamelCase tokens wrap instead of forcing horizontal scroll.
 */
export default function InfoTip({
  k,                 // key into WHATIF_GLOSSARY
  term,              // explicit override
  def,               // explicit override
  children,
  className = '',
  iconSize = 10,
  showIcon = true,   // render the small "i" affordance
  underline = true,  // render the dotted underline affordance
  maxWidth = 260,
  side = 'auto',     // 'top' | 'bottom' | 'auto'
  testId,
}) {
  const entry = k ? WHATIF_GLOSSARY[k] : null;
  const finalTerm = term || entry?.term || '';
  const finalDef = def || entry?.def || '';
  const wrapperRef = useRef(null);
  const timerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0, flipUp: false, width: maxWidth });
  const id = useId();

  const updatePosition = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    // Effective bubble width — capped at viewport width minus gutters so
    // the bubble can never outgrow the screen on phones.
    const w = Math.min(maxWidth, viewportW - 16);
    // Desired center on the trigger's midline.
    const cx = rect.left + rect.width / 2;
    // Clamp so the bubble stays fully inside the viewport.
    let left = Math.round(cx - w / 2);
    if (left < 8) left = 8;
    if (left + w > viewportW - 8) left = viewportW - w - 8;
    // Flip above the trigger when there's not enough space below.
    const belowSpace = viewportH - rect.bottom;
    const flipUp = side === 'top' || (side === 'auto' && belowSpace < 140);
    const top = flipUp ? rect.top - 8 : rect.bottom + 8;
    setPos({ left, top, flipUp, width: w });
  }, [maxWidth, side]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const handler = () => updatePosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open, updatePosition]);

  const show = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setOpen(true);
  }, []);
  const hide = useCallback(() => setOpen(false), []);
  const touchShow = useCallback((e) => {
    e.stopPropagation();
    show();
    // Auto-close on mobile so the tooltip doesn't get stuck open.
    timerRef.current = setTimeout(() => setOpen(false), 2500);
  }, [show]);

  if (!finalDef) {
    // No definition available — render children unchanged so layout is preserved.
    return <>{children}</>;
  }

  return (
    <>
      <span
        ref={wrapperRef}
        className={`relative inline-flex items-center gap-0.5 cursor-help ${underline ? 'underline decoration-dotted decoration-muted-foreground/50 underline-offset-2' : ''} ${className}`}
        data-testid={testId}
        tabIndex={0}
        aria-describedby={open ? id : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onTouchStart={touchShow}
      >
        {children}
        {showIcon && (
          <Info className="shrink-0 text-muted-foreground/70" style={{ width: iconSize, height: iconSize }} />
        )}
      </span>
      {open && createPortal(
        <div
          id={id}
          role="tooltip"
          data-testid={testId ? `${testId}-bubble` : undefined}
          className="fixed z-[200] pointer-events-none rounded-md shadow-xl border border-border bg-popover text-popover-foreground px-3 py-2 text-[11px] leading-snug"
          style={{
            left: pos.left,
            top: pos.flipUp ? undefined : pos.top,
            bottom: pos.flipUp ? (window.innerHeight - pos.top) : undefined,
            width: pos.width,
            maxWidth: 'calc(100vw - 16px)',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            whiteSpace: 'normal',
          }}
        >
          {finalTerm && (
            <div className="font-bold text-foreground mb-0.5">{finalTerm}</div>
          )}
          <div className="text-muted-foreground">{finalDef}</div>
        </div>,
        document.body,
      )}
    </>
  );
}
